// import 'fullpage.js/vendors/scrolloverflow'
// Import the old version of scrolloverflow plugin used on the official documentation site.
// Solving the bug with scrolling in IE
import './scrolloverflow'

// import fullpage from 'fullpage.js'
// Temporarily import the locally altered file copied from PR of fullpage
// Issue: https://github.com/alvarotrigo/fullPage.js/issues/3527
// PR fork: https://github.com/AllirionX/fullPage.js/blob/9bd9842720467aaa8c155849db83953dcd1ec7aa/src/fullpage.js
import './fullpage-temp'

import throttle from 'lodash.throttle'
import {
    FULL_PAGE_LICENSE_KEY,
    SCROLLING_SPEED,
    SCROLLING_SPEED_FIRST_SECTIONS,
} from 'constants'
import { mq } from 'utilities/breakpoints'
import { classes, pageLoader } from 'constants'
import ActiveItems from './ActiveItems'
import VideoMower from 'components/VideoMower'
import InfoMower from 'components/InfoMower'

class FullPage extends ActiveItems {
    constructor(container) {
        const activeItems = container.querySelector('.js-fullpage-active-items')
        const anchorButtons = [...container.querySelectorAll('.js-anchor')]
        super(activeItems)

        this.preventOverflowSectionSlide = true

        this.handleBulletClick = throttle(
            this.handleBulletClick.bind(this),
            SCROLLING_SPEED,
            { trailing: false }
        )

        this.setActiveSection = this.setActiveSection.bind(this)
        this.getBulletId = this.getBulletId.bind(this)
        this.create = this.create.bind(this)

        this.defaultProps = {
            licenseKey: FULL_PAGE_LICENSE_KEY,
            dragAndMove: false,
            verticalCentered: true,
            scrollOverflow: true,
            scrollOverflowOptions: {
                scrollbars: false,
                disablePointer: true,
            },
            scrollingSpeed: SCROLLING_SPEED,
            onLeave: (origin, destination) => {
                if (
                    this.fullpage &&
                    origin.item.querySelector('.fp-scrollable') &&
                    this.preventOverflowSectionSlide
                ) {
                    this.preventOverflowSectionSlide = false

                    // Disable scrolling
                    this.fullpage.setAllowScrolling(false)
                    this.fullpage.setKeyboardScrolling(false)

                    setTimeout(() => {
                        // Enable scrolling
                        this.fullpage.setAllowScrolling(true)
                        this.fullpage.setKeyboardScrolling(true)
                    }, 1000)

                    return false
                }

                this.setActiveSection(origin, destination)
                this.runVideo(origin, destination)
                this.handleFirstSectionsTransition(origin, destination)
                this.fadeOutInfoMower(origin, destination)
            },
            afterLoad: (origin, destination) => {
                this.fadeInInfoMower(origin, destination)
                this.preventOverflowSectionSlide = true
            },
        }

        this.container = container
        this.mowerVideo = FullPage.setMowerVideo()
        this.mowerInfo = FullPage.setMowerInfo()
        this.bullets = this.container.querySelector('.js-fullpage-bullets')
        this.bulletsList = this.container.querySelectorAll(
            '.js-fullpage-bullet'
        )
        this.bulletArrowUp = this.bullets.querySelector('.js-fullpage-arrow-up')
        this.bulletArrowDown = this.bullets.querySelector(
            '.js-fullpage-arrow-down'
        )
        this.content = this.container.querySelector('.js-fullpage-content')

        this.header = document.querySelector('header')
        this.headerParent = document.querySelector('.js-header-parent')

        this.mediaBreakpoint = window.matchMedia(mq['SM_ONLY'])
        this.mediaBreakpointMobile = window.matchMedia(mq['SM_MAX'])
        this.mediaBreakpoint.addListener(this.handleHeaderDOMPosition)
        this.mediaBreakpointMobile.addListener(this.handleFullpageBuild)

        anchorButtons.forEach(button =>
            button.addEventListener('click', () => {
                this.preventOverflowSectionSlide = false
            })
        )

        window.addEventListener('formValidated', () => this.fullpage.reBuild())

        this.handleHeaderDOMPosition()
        this.handleFullpageBuild()
    }

    static setMowerVideo() {
        const videoMowerHTMLElement = document.querySelector('.js-video-mower')
        if (!videoMowerHTMLElement) return null
        const videoMower = new VideoMower(videoMowerHTMLElement)

        const { video } = videoMower

        video.addEventListener('pause', () => {
            if (video.currentTime !== video.duration) {
                const pointerContainer = document.querySelector('.js-precision')
                pointerContainer.classList.add('in-dom')
                setTimeout(() => {
                    pointerContainer.classList.add('visible')
                }, 50)
            }
        })

        video.addEventListener('play', () => {
            const pointerContainer = document.querySelector('.js-precision')
            pointerContainer.classList.remove('visible')
            pointerContainer.classList.remove('in-dom')
        })

        return videoMower
    }

    static setMowerInfo() {
        const infoMowerHTMLElement = document.querySelectorAll('.js-mower-info')
        if (!infoMowerHTMLElement.length) return null
        return new InfoMower(infoMowerHTMLElement)
    }

    fadeOutInfoMower(_origin, destination) {
        if (destination.index === 0) {
            this.mowerInfo.fadeOutEndContainer()
        } else if (destination.index === 1) {
            this.mowerInfo.fadeOutStartContainer()
        }
    }

    fadeInInfoMower(_origin, destination) {
        if (destination.index === 0) {
            this.mowerInfo.fadeInStartContainer()
        } else if (destination.index === 1) {
            this.mowerInfo.fadeInEndContainer()
        }
    }

    handleHeaderDOMPosition = () => {
        const targetWrapper = this.mediaBreakpoint.matches
            ? this.headerParent // Move header into headerParent on mobiles
            : document.body // Move header into body on higher resolutions to be fixed positioned

        targetWrapper.insertBefore(this.header, targetWrapper.firstChild)
    }

    handleFullpageBuild = () => {
        const isMobile = this.mediaBreakpointMobile.matches

        if (isMobile) {
            if (this.fullpage) this.fullpage.destroy('all')
        } else {
            this.initFullpage()
        }
    }

    handleFirstSectionsTransition = (origin, destination) => {
        if (this.fullpage) {
            // Check sliding for first two sections
            const isFromFirstToSecond =
                origin.index === 0 && destination.index === 1
            const isFromSecondToFirst =
                origin.index === 1 && destination.index === 0

            // Slower scroll speed for the first two sections
            const scrollSpeed =
                isFromFirstToSecond || isFromSecondToFirst
                    ? SCROLLING_SPEED_FIRST_SECTIONS
                    : SCROLLING_SPEED

            if (isFromSecondToFirst) {
                // Hide interactive points when going from 2nd to 1st section
                const pointerContainer = document.querySelector('.js-precision')
                pointerContainer.classList.remove('visible')
            }

            this.fullpage.setScrollingSpeed(scrollSpeed)
        }
    }

    runVideo = (_origin, destination) => {
        if (destination.index === 0) {
            this.slideVideoUp()
        } else if (destination.index === 1) {
            this.slideVideoDown()
        }
    }

    slideVideoDown = () => {
        this.mowerVideo.setVideoOnStart()
        this.mowerVideo.playHalfVideo()
        this.mowerVideo.moveDown()
    }

    slideVideoUp = () => {
        this.mowerVideo.setVideoOnHalf()
        this.mowerVideo.playBackwardsVideo()
        this.mowerVideo.moveUp()
    }

    initFullpage = () => {
        this.create({
            ...this.defaultProps,
        })
    }

    create(props) {
        this.fullpage = new window.fullpage(this.content, props)

        const { body } = document

        this.fullpage.setAllowScrolling(
            !body.classList.contains(pageLoader.bodyClassName)
        )

        this.bodyObserver = new MutationObserver(mutations => {
            mutations.forEach(() => {
                this.fullpage.setAllowScrolling(
                    !body.classList.contains(pageLoader.bodyClassName)
                )
            })
        })

        this.bodyObserver.observe(body, { attributes: true })

        // set default active styles
        this.setActiveSection()
        this.bulletsList.forEach(bullet =>
            bullet.addEventListener('click', this.handleBulletClick)
        )
    }

    getBulletId = anchor => `[data-fullpage-bullet-id="${anchor}"]`

    styleArrows = index => {
        if (index === 0) {
            this.bulletArrowUp.classList.add(classes.HIDDEN)
            this.bulletArrowDown.classList.remove(classes.HIDDEN)
        } else if (
            /**
             * Hide when
             * last element (consider arrows)
             * prelast element (consider arrows and last element)
             */
            index === this.bulletsList.length - 2 ||
            index === this.bulletsList.length - 2 - 1
        ) {
            this.bulletArrowUp.classList.remove(classes.HIDDEN)
            this.bulletArrowDown.classList.add(classes.HIDDEN)
        } else {
            this.bulletArrowUp.classList.remove(classes.HIDDEN)
            this.bulletArrowDown.classList.remove(classes.HIDDEN)
        }
    }

    setActiveSection(origin, destination = this.fullpage.getActiveSection()) {
        this.styleArrows(destination.index)

        if (!destination.anchor) return
        if (origin) {
            const activeBullet = this.bullets.querySelector('.active')
            if (activeBullet) activeBullet.classList.remove('active')
        }

        const activeBullet = this.bullets.querySelector(
            this.getBulletId(destination.anchor)
        )

        if (activeBullet) activeBullet.classList.add('active')

        this.ActiveItems.go(`=${destination.index}`)
    }

    handleBulletClick = ({ currentTarget }) => {
        const { fullpageBulletId } = currentTarget.dataset
        if (fullpageBulletId) {
            switch (fullpageBulletId) {
                case 'up':
                    this.fullpage.moveSectionUp()
                    break

                case 'down':
                    this.fullpage.moveSectionDown()
                    break

                default:
                    this.fullpage.moveTo(fullpageBulletId)
                    break
            }
        }
    }
}

export default FullPage
